import { z } from 'zod';
import { request, useMutation } from '@applyboard/ui-utils';
import { NewVasError } from '../common/errors/NewVasError';
import { getAccessToken } from '../common/utils/accessToken';

const REQUEST_INFORMATION_V1_URL = '/api/vas_hub/v1/request_information';

const RequestInformationSchema = z.object({
  rp_id: z.string().optional(),
  name: z.string(),
  email: z.string().email(),
  service: z.string(),
  vendor: z.string(),
  inquiry_details: z.string(),
});

export type RequestInformation = z.infer<typeof RequestInformationSchema>;

export function useRequestInformation() {
  const { mutate, mutateAsync, isLoading, isError } = useMutation(
    async (requestInformation: RequestInformation) => {
      const token = await getAccessToken(false);
      return request(REQUEST_INFORMATION_V1_URL, {
        method: 'POST',
        authorization: token ? `Bearer ${token}` : undefined,
        schema: z.null(),
        body: requestInformation,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new NewVasError(jsonMessage, 'Request for more information failed');
        },
      });
    },
  );

  const requestInformation = (
    requestInformation: RequestInformation,
    onSuccess?: () => void,
    onError?: (error: unknown) => void,
  ) =>
    mutate(requestInformation, {
      onSuccess: () => onSuccess?.(),
      onError: (error: unknown) => onError?.(error),
    });

  const requestInformationAsync = async (requestInformation: RequestInformation) =>
    mutateAsync(requestInformation);

  return {
    requestInformation,
    requestInformationAsync,
    isRequestingInformation: isLoading,
    isRequestInformationError: isError,
  };
}
